import { createContext, useCallback, useContext, useState } from 'react'
import Modal from './Modal'
import Button from './Button'

const DialogContext = createContext(null)

/**
 * Sostituisce confirm() e prompt() del browser (bloccanti, senza stile, non
 * annullabili con Esc in modo coerente col resto dell'app) con un'unica
 * finestra modale — punto u4 della checklist di raffinamento (37 confirm e
 * 10 prompt sparsi nel codice). Come Modal, la cornice sta qui; il messaggio
 * e le etichette restano decisi da chi chiama.
 *
 * const confirm = useConfirm()
 * if (!(await confirm('Eliminare questa voce?'))) return
 *
 * const prompt = usePrompt()
 * const note = await prompt('Nota per il PM:', '')
 * if (note === null) return // annullato, come window.prompt
 */
export function DialogProvider({ children }) {
  const [state, setState] = useState(null)
  // state: { kind: 'confirm'|'prompt', message, resolve, ...opts }

  const confirm = useCallback(
    (message, opts = {}) =>
      new Promise((resolve) => {
        setState({ kind: 'confirm', message, resolve, ...opts })
      }),
    [],
  )

  const prompt = useCallback(
    (message, defaultValue = '', opts = {}) =>
      new Promise((resolve) => {
        setState({ kind: 'prompt', message, defaultValue, resolve, ...opts })
      }),
    [],
  )

  const close = (result) => {
    state?.resolve(result)
    setState(null)
  }

  return (
    <DialogContext.Provider value={{ confirm, prompt }}>
      {children}
      {state?.kind === 'confirm' && <ConfirmBox state={state} onClose={close} />}
      {state?.kind === 'prompt' && <PromptBox state={state} onClose={close} />}
    </DialogContext.Provider>
  )
}

function ConfirmBox({ state, onClose }) {
  const {
    message, title, confirmLabel = 'Conferma', cancelLabel = 'Annulla', tone = 'default',
  } = state
  return (
    <Modal onClose={() => onClose(false)} title={title} size="sm">
      <div className="px-5 py-4">
        <p className="text-sm text-gray-700 whitespace-pre-line">{message}</p>
      </div>
      <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => onClose(false)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
        >
          {cancelLabel}
        </button>
        <Button
          variant={tone === 'danger' ? 'danger' : 'primary'}
          size="sm"
          onClick={() => onClose(true)}
          autoFocus
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  )
}

function PromptBox({ state, onClose }) {
  const {
    message, title, confirmLabel = 'OK', cancelLabel = 'Annulla',
    defaultValue = '', type = 'text', placeholder,
  } = state
  // come window.prompt: il valore risolto è sempre una stringa, anche se il
  // chiamante ha passato un numero come default (es. un anno) senza toccarlo
  const [value, setValue] = useState(String(defaultValue ?? ''))
  const submit = (e) => {
    e?.preventDefault()
    onClose(value)
  }
  return (
    <Modal onClose={() => onClose(null)} title={title} size="sm">
      <form onSubmit={submit}>
        <div className="px-5 py-4 space-y-2">
          <label htmlFor="dialog-prompt-input" className="block text-sm text-gray-700 whitespace-pre-line">
            {message}
          </label>
          <input
            id="dialog-prompt-input"
            type={type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onClose(null)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {cancelLabel}
          </button>
          <Button type="submit" size="sm">
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function useDialogs() {
  const ctx = useContext(DialogContext)
  if (ctx) return ctx
  // Fallback se DialogProvider non è montato (testing / componente isolato):
  // stesso comportamento di window.confirm/prompt, mai bloccante di più.
  return {
    confirm: async (message) => window.confirm(message),
    prompt: async (message, defaultValue) => window.prompt(message, defaultValue),
  }
}

export function useConfirm() {
  return useDialogs().confirm
}

export function usePrompt() {
  return useDialogs().prompt
}
